import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Mail, Filter as FilterIcon, Inbox,
  FileText, Settings, LogOut, User, Zap, Webhook, Shield,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/emails", label: "Emails", icon: Inbox, testid: "nav-emails" },
  { to: "/filters", label: "Filtres", icon: FilterIcon, testid: "nav-filters" },
  { to: "/summaries", label: "Résumés IA", icon: FileText, testid: "nav-summaries" },
  { to: "/account", label: "Comptes email", icon: Mail, testid: "nav-account" },
  { to: "/webhooks", label: "Webhooks", icon: Webhook, testid: "nav-webhooks" },
  { to: "/settings", label: "Paramètres", icon: Settings, testid: "nav-settings" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Administration", icon: Shield, testid: "nav-admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="font-semibold text-gray-900 tracking-tight" style={{fontFamily:"'Work Sans', sans-serif"}}>
              SmartMail<span className="text-[#FF3B30]">.AI</span>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                  isActive
                    ? "bg-gray-50 text-[#002FA7] border-[#002FA7]"
                    : "text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-900"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="overline px-3 pt-4 pb-1">// Admin</div>
              {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                      isActive
                        ? "bg-red-50 text-[#FF3B30] border-[#FF3B30]"
                        : "text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-900"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center text-xs font-semibold">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={() => nav("/profile")}
              data-testid="nav-profile"
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-gray-700 border border-gray-300 hover:border-gray-900 transition-colors"
            >
              <User className="w-3 h-3" /> Profil
            </button>
            <button
              onClick={() => { logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-red-700 border border-red-200 hover:border-red-700 transition-colors"
            >
              <LogOut className="w-3 h-3" /> Sortir
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
